<script setup lang="ts">
import { computed, ref } from 'vue'

interface LevelTreeData {
  level: number
  xp_requirement: number
  required_average: number
  rank: string | null
}

const { data } = defineProps<{
  data: ProgressionData
}>()

const showAllLevels = ref(false)

const getTimelineEntryColor = (levelData: LevelTreeData): string => {
  if (!data) return 'bg-gray-400'
  if (data.current_level === levelData.level) return 'bg-primary ring-primary'
  if (data.current_level + 1 === levelData.level) return 'bg-orange-500 ring-orange-500'
  if (data.current_level >= levelData.level) return 'bg-green-500 ring-green-500'
  return '!bg-grey-400'
}

const getTimelineEntryIcon = (levelData: LevelTreeData): string => {
  if (!data) return 'tabler-circle'
  if (data.current_level === levelData.level) return 'tabler-star'
  if (data.current_level + 1 === levelData.level) return 'tabler-arrow-right'
  if (data.current_level >= levelData.level) return 'tabler-circle-check'
  return 'tabler-circle'
}

const filteredLevelTree = computed(() => {
  if (!data) return []

  const levels = Object.values(data.level_tree)
  const maxLevel = Math.max(...levels.map(l => l.level))

  return levels.filter((levelData) => {
    const level = levelData.level
    return level === 1 // First level
      || level === maxLevel // Max level
      || level === data.current_level // Current level
      || Math.abs(level - data.current_level) <= 2 // Up to 2 levels above/below current
  }).sort((a, b) => a.level - b.level)
})

const displayedLevelTree = computed(() => {
  if (!data) return []
  const levels = Object.values(data.level_tree)

  if (showAllLevels.value) {
    return levels.sort((a, b) => a.level - b.level)
  }

  return filteredLevelTree.value
})

const hiddenSections = computed(() => {
  if (!data || showAllLevels.value) return []

  const levels = Object.values(data.level_tree).sort((a, b) => a.level - b.level)
  const displayed = new Set(filteredLevelTree.value.map(l => l.level))

  const sections = []
  let currentSection = null

  for (let i = 0; i < levels.length; i++) {
    const level = levels[i]?.level
    if (level && !displayed.has(level)) {
      if (!currentSection) {
        currentSection = { start: levels[i - 1]?.level || 0, end: level, count: 1 }
      }
      else {
        currentSection.end = level
        currentSection.count++
      }
    }
    else if (currentSection) {
      sections.push(currentSection)
      currentSection = null
    }
  }

  if (currentSection) {
    sections.push(currentSection)
  }

  return sections
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <UiAlert
      v-if="!data.can_gain_xp"
      variant="warning"
      :title="$t('Xp limit reached. Play the challenge to gain the next ranking.')"
      :description="$t('You can still practice, but no additional xp can be gained until you have reached the next rank.')"
    />
    <div class="flex flex-col gap-4 items-start">
      <div class="w-full grid grid-cols-2 gap-4">
        <div class="text-h5">
          Level {{ data.current_level }}
          <div class="text-subtitle1">
            {{ data.current_xp }} / {{ data.xp_for_next_rank }} XP
            <div class="text-caption">
              {{ data.xp_remaining_for_next_rank }} XP remaining to next rank
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2 justify-center">
          <div class="text-h6">
            {{ $t('Current Rank') }}:
            <span class="text-primary">{{ data.current_rank || $t('Unranked') }}</span>
          </div>

          <!-- Next Rank Requirements -->
          <div
            v-if="data.next_rank"
            class="text-subtitle1"
          >
            {{ $t('Next Rank') }}: {{ data.next_rank }}
            <div class="text-caption">
              {{ $t('Required Average') }}: {{ data.required_average }}
            </div>
          </div>
        </div>
      </div>
      <UiProgress
        :model-value="data.progress_percentage"
        :max="100"
      />
    </div>

    <UiSeparator />

    <!-- Milestone Timeline -->
    <div class="flex flex-col gap-2">
      <div class="flex justify-end">
        <UiButton
          :text="showAllLevels ? $t('Show Less') : $t('Show All Levels')"
          @click="showAllLevels = !showAllLevels"
        />
      </div>

      <div
        :model-value="data.current_level"
        class="mx-auto flex w-full max-w-md flex-col justify-start gap-5"
      >
        <template
          v-for="(levelData, index) in displayedLevelTree"
          :key="levelData.level"
        >
          <!-- Hidden Levels Indicator -->
          <div
            v-if="!showAllLevels && index > 0"
            class=""
          >
            <template
              v-for="section in hiddenSections"
              :key="`${section.start}-${section.end}`"
            >
              <div
                v-if="section.start < levelData.level && section.end > displayedLevelTree[index-1]!.level"
                class="text-left text-grey-6 flex flex-row gap-2 mt-8 items-center text-gray-500 ml-8"
              >
                <Icon name="tabler-plus" />
                {{ $t('{n} levels', { n: section.count }) }}
              </div>
            </template>
          </div>

          <div
            :step="levelData.level"
            class="group relative flex w-full items-start gap-6"
          >
            <div
              v-if="levelData.level !== 50"
              class="absolute left-[18px] top-[38px] block h-[105%] w-0.5 shrink-0 rounded-full bg-muted group-data-[state=completed]:bg-primary"
            />
            <UiButton
              size="icon"
              class="z-10 shrink-0 rounded-full pointer-events-none"
              :disabled="data.current_level < levelData.level"
              :class="[data.current_level >= levelData.level && 'ring-2 ring-ring ring-offset-2 ring-offset-background ', getTimelineEntryColor(levelData)]"
            >
              <Icon
                :name="getTimelineEntryIcon(levelData)"
              />
            </UiButton>
            <div class="flex flex-col gap-1">
              <div>
                {{ $t('Level {level} {rank}', { level: levelData.level, rank: levelData.rank }) }}
                <div class="text-gray-500">
                  {{ $t('Required XP: {xp}', { xp: levelData.xp_requirement }) }}
                </div>
              </div>
              <div>
                <div
                  v-if="data.current_level === levelData.level && data.current_rank === levelData.rank"
                  class="text-primary"
                >
                  <UiBadge>
                    {{ $t('Current Level') }}
                  </UiBadge>
                </div>
                <div
                  v-else-if="data.current_level + 1 === levelData.level"
                  class="text-orange"
                >
                  <UiBadge variant="warning">
                    {{ $t('Next Level') }}
                  </UiBadge>
                </div>
                <div
                  v-else-if="data.next_rank === levelData.rank"
                  class="text-primary-500"
                >
                  <strong>{{ $t('Complete the challenges to progress to the {rank} rank', { rank: levelData.rank }) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
